import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../api/axios';
import { useChurch } from '../context/ChurchContext';
import useGlobalView from '../hooks/useGlobalView';
import ChurchBadge from '../components/common/ChurchBadge';

const CARD = {
  background: 'var(--card-bg)', borderRadius: '14px', border: '1px solid var(--border)',
  boxShadow: 'var(--card-shadow)', padding: '18px 20px', marginBottom: '16px',
};

// Couleur selon la fiabilité du critère (cf. backend apps/participants/duplicates.py)
const LEVEL = {
  name_phone:  { bg: '#fee2e2', color: '#991b1b' },
  email:       { bg: '#ffedd5', color: '#9a3412' },
  name_church: { bg: '#fef3c7', color: '#92400e' },
  phone:       { bg: '#f1f5f9', color: '#475569' },
};

const STATUS_LABELS = { nouveau: 'Nouveau', membre: 'Membre régulier', serviteur: 'Serviteur', invite: 'Invité' };

// Fiche proposée par défaut à garder : la plus de présences, puis la plus ancienne
const defaultKeep = (members) =>
  [...members].sort((a, b) => (b.presences - a.presences) || (new Date(a.created_at) - new Date(b.created_at)))[0].id;

function MergePanel({ group, onMerged }) {
  const [keep, setKeep] = useState(() => defaultKeep(group.members));
  // « Même numéro uniquement » = souvent une famille : rien n'est coché par défaut
  const [selected, setSelected] = useState(() => new Set(group.criterion === 'phone' ? [] : group.members.map(m => m.id)));
  const [confirming, setConfirming] = useState(false);
  const [merging, setMerging] = useState(false);
  const toMerge = group.members.filter(m => m.id !== keep && selected.has(m.id));
  const kept = group.members.find(m => m.id === keep);

  const doMerge = async () => {
    setMerging(true);
    try {
      const res = await api.post('/participants/merge/', { keep, merge: toMerge.map(m => m.id) });
      toast.success(`Fusion faite : ${res.data.presences} présence(s) reprise(s). Les anciennes cartes restent valables.`);
      onMerged();
    } catch (err) {
      if (err.response?.status === 400) toast.error(err.response.data?.detail || 'Fusion impossible.');
    } finally {
      setMerging(false);
      setConfirming(false);
    }
  };

  return (
    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px dashed var(--border)' }}>
      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '8px' }}>
        {group.criterion === 'phone'
          ? 'Même numéro, noms différents : cochez une fiche seulement si c’est la même personne (sinon c’est sans doute une famille, ne fusionnez pas).'
          : 'Fiche à garder (●) et fiches à fusionner dedans (☑) :'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
        {group.members.map(m => (
          <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', padding: '5px 10px', borderRadius: '8px', border: `1.5px solid ${m.id === keep ? '#1e40af' : 'var(--border)'}`, background: m.id === keep ? '#eff6ff' : '#fff' }}>
            <input type="radio" name={`keep-${group.members[0].id}`} checked={m.id === keep} onChange={() => setKeep(m.id)} />
            {m.id !== keep && (
              <input type="checkbox" checked={selected.has(m.id)}
                onChange={e => setSelected(prev => { const n = new Set(prev); e.target.checked ? n.add(m.id) : n.delete(m.id); return n; })} />
            )}
            {m.member_code}
          </label>
        ))}
      </div>
      {!confirming ? (
        <button disabled={toMerge.length === 0} onClick={() => setConfirming(true)}
          style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#b91c1c', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: toMerge.length ? 'pointer' : 'default', opacity: toMerge.length ? 1 : 0.5 }}>
          Fusionner {toMerge.length} fiche(s) dans {kept?.member_code}
        </button>
      ) : (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '0.84rem', color: '#991b1b' }}>
            <strong>Action définitive.</strong> {toMerge.map(m => m.member_code).join(', ')} {toMerge.length > 1 ? 'seront supprimées' : 'sera supprimée'} ;
            leurs présences et informations passent sur <strong>{kept?.member_code} ({kept?.full_name})</strong>.
            Leurs cartes et codes continueront de fonctionner.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={doMerge} disabled={merging}
              style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#b91c1c', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
              {merging ? 'Fusion...' : 'Confirmer la fusion'}
            </button>
            <button onClick={() => setConfirming(false)} disabled={merging}
              style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Fusion groupée des doublons quasi certains (même nom ET même numéro) :
// aperçu obligatoire, puis confirmation explicite.
function MergeAllPanel({ onDone }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const preview = async () => {
    setLoading(true);
    try {
      const res = await api.post('/participants/merge-all/', { dry_run: true });
      setPlan(res.data);
    } catch { /* erreurs signalées par l'intercepteur */ } finally { setLoading(false); }
  };

  const confirm = async () => {
    setLoading(true);
    try {
      const res = await api.post('/participants/merge-all/', { dry_run: false });
      toast.success(`${res.data.merged} fiche(s) fusionnée(s). Les anciennes cartes restent valables.`);
      setPlan(null);
      onDone();
    } catch { /* erreurs signalées par l'intercepteur */ } finally { setLoading(false); }
  };

  const total = plan ? plan.groups.reduce((n, g) => n + g.merge.length, 0) : 0;
  return (
    <div style={{ ...CARD, borderColor: '#fecaca' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>Fusionner tous les doublons quasi certains</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 2 }}>
            Uniquement les fiches avec le <strong>même nom et le même numéro</strong>. La fiche gardée est celle qui a le plus de présences.
          </div>
        </div>
        {!plan && (
          <button onClick={preview} disabled={loading}
            style={{ padding: '8px 14px', borderRadius: '8px', border: '1.5px solid #fecaca', background: '#fff', color: '#b91c1c', fontWeight: 700, fontSize: '0.84rem', cursor: 'pointer' }}>
            {loading ? 'Analyse...' : "Voir l'aperçu"}
          </button>
        )}
      </div>
      {plan && (
        <div style={{ marginTop: '12px' }}>
          {plan.groups.length === 0 ? (
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Aucun doublon quasi certain à fusionner.</p>
          ) : (
            <>
              <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '10px' }}>
                {plan.groups.map((g, i) => (
                  <div key={i} style={{ padding: '8px 12px', borderTop: i ? '1px solid var(--border)' : 'none', fontSize: '0.82rem' }}>
                    Garder <strong>{g.keep.full_name}</strong> ({g.keep.member_code}, {g.keep.presences} présence(s))
                    {' ← '}{g.merge.map(m => `${m.member_code} (${m.presences})`).join(', ')}
                  </div>
                ))}
              </div>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px' }}>
                <p style={{ margin: '0 0 10px', fontSize: '0.84rem', color: '#991b1b' }}>
                  <strong>Action définitive :</strong> {total} fiche(s) seront supprimées et leurs données reprises par la fiche gardée.
                  Leurs cartes et codes continueront de fonctionner. Chaque fusion est enregistrée dans le Journal.
                </p>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={confirm} disabled={loading}
                    style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#b91c1c', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                    {loading ? 'Fusion...' : `Confirmer la fusion de ${total} fiche(s)`}
                  </button>
                  <button onClick={() => setPlan(null)} disabled={loading}
                    style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                    Annuler
                  </button>
                </div>
              </div>
            </>
          )}
          {plan.skipped_multi_church > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              {plan.skipped_multi_church} groupe(s) répartis sur plusieurs églises ne sont pas inclus : fusionnez-les un par un.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function Duplicates() {
  const navigate = useNavigate();
  const { activeChurchId } = useChurch();
  const isGlobal = useGlobalView();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Numéro partagé avec un nom différent : affiché par défaut, fusion proposée (rien de coché)
  const [showPhoneOnly, setShowPhoneOnly] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const deleteParticipant = async () => {
    setDeleting(true);
    try {
      await api.delete(`/participants/${deleteTarget.id}/`);
      toast.success(`Fiche ${deleteTarget.member_code} supprimée.`);
      setDeleteTarget(null);
      fetchData();
    } catch { /* erreurs signalées par l'intercepteur */ } finally { setDeleting(false); }
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    setError(false);
    api.get('/participants/duplicates/')
      .then(res => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData, activeChurchId]);

  const groups = (data?.groups || []).filter(g => showPhoneOnly || g.criterion !== 'phone');
  const phoneOnlyCount = (data?.groups || []).filter(g => g.criterion === 'phone').length;

  return (
    <div style={{ width: '100%', maxWidth: '980px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '1.6rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Doublons de fidèles</h1>
        <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.9rem' }}>
          Fiches qui semblent correspondre à la même personne. Vérifiez avant de fusionner : un homonyme ou un numéro de famille n'est pas un doublon.
        </p>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-secondary)' }}>Analyse en cours...</p>
      ) : error ? (
        <div style={CARD}>
          <p style={{ margin: '0 0 10px', color: '#b91c1c' }}>Le rapport ne peut pas être chargé pour le moment.</p>
          <button onClick={fetchData} style={{ background: '#1e40af', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 700, cursor: 'pointer' }}>Réessayer</button>
        </div>
      ) : (
        <>
          <div style={{ ...CARD, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)' }}>
              <strong>{data.analysed}</strong> fiche(s) analysée(s) · <strong>{groups.length}</strong> groupe(s) affiché(s)
            </span>
            {phoneOnlyCount > 0 && (
              <label style={{ fontSize: '0.84rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                <input type="checkbox" checked={showPhoneOnly} onChange={e => setShowPhoneOnly(e.target.checked)} />
                Afficher les numéros partagés avec un nom différent ({phoneOnlyCount}) — à vérifier : même personne ou famille ?
              </label>
            )}
          </div>

          <MergeAllPanel onDone={fetchData} />

          {groups.length === 0 ? (
            <div style={CARD}><p style={{ margin: 0, color: 'var(--text-secondary)' }}>Aucun doublon probable.</p></div>
          ) : groups.map((g, i) => {
            const lvl = LEVEL[g.criterion] || LEVEL.phone;
            return (
              <div key={i} style={CARD}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
                  <span style={{ padding: '3px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 700, background: lvl.bg, color: lvl.color }}>
                    {g.confidence}
                  </span>
                  <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{g.label}</span>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '0.84rem' }}>· {g.members.length} fiches</span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '6px 8px' }}>Code</th>
                        <th style={{ padding: '6px 8px' }}>Nom</th>
                        {isGlobal && <th style={{ padding: '6px 8px' }}>Église</th>}
                        <th style={{ padding: '6px 8px' }}>Téléphone</th>
                        <th style={{ padding: '6px 8px' }}>Statut</th>
                        <th style={{ padding: '6px 8px' }}>Présences</th>
                        <th style={{ padding: '6px 8px' }}>Créée le</th>
                        <th style={{ padding: '6px 8px' }} />
                      </tr>
                    </thead>
                    <tbody>
                      {g.members.map(m => (
                        <tr key={m.id} onClick={() => navigate(`/participants/${m.id}`)}
                          style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}>
                          <td style={{ padding: '8px', fontWeight: 700, color: '#1e40af' }}>{m.member_code}</td>
                          <td style={{ padding: '8px' }}>{m.full_name}</td>
                          {isGlobal && <td style={{ padding: '8px' }}><ChurchBadge name={m.church_name} /></td>}
                          <td style={{ padding: '8px' }}>{m.phone || '—'}</td>
                          <td style={{ padding: '8px' }}>{STATUS_LABELS[m.status] || m.status}</td>
                          <td style={{ padding: '8px', fontWeight: 700 }}>{m.presences}</td>
                          <td style={{ padding: '8px' }}>{new Date(m.created_at).toLocaleDateString('fr-FR')}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>
                            <button onClick={e => { e.stopPropagation(); setDeleteTarget(m); }}
                              style={{ padding: '4px 10px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', fontWeight: 700, fontSize: '0.74rem', cursor: 'pointer' }}>
                              Supprimer
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {g.members.some(m => m.id === deleteTarget?.id) && (
                  <div style={{ marginTop: '10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px' }}>
                    <p style={{ margin: '0 0 10px', fontSize: '0.84rem', color: '#991b1b' }}>
                      <strong>Supprimer définitivement {deleteTarget.full_name} ({deleteTarget.member_code}) ?</strong> Ses {deleteTarget.presences} présence(s)
                      sont supprimées avec la fiche et sa carte ne fonctionnera plus. Si c'est la même personne qu'une autre fiche, préférez la <strong>fusion</strong>.
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={deleteParticipant} disabled={deleting}
                        style={{ padding: '7px 14px', borderRadius: '8px', border: 'none', background: '#b91c1c', color: '#fff', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                        {deleting ? 'Suppression...' : 'Confirmer la suppression'}
                      </button>
                      <button onClick={() => setDeleteTarget(null)} disabled={deleting}
                        style={{ padding: '7px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: '#fff', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}>
                        Annuler
                      </button>
                    </div>
                  </div>
                )}
                <MergePanel key={g.members.map(m => m.id).join('-')} group={g} onMerged={fetchData} />
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
